import { createElement, type ComponentType, useEffect, useRef } from 'react'
import { useFeatureFlagBoolean } from '@/services/feature/useFeatureQueries'
import Loader from '@/components/content/Loader'
import Button from '@/components/content/Button'
import { useQuery } from '@tanstack/react-query'
import { getShipperListV2 } from '@/services/shipper/apis'
import type { ShipperItem } from '@/services/shipper/types'
import { notice } from '@/components/pbo/notice'
import { MESSAGES } from '@/pages/return/returnInquiryAll/constants'

const TARGET_SHIPPERS = new Set(['MUSINSA', '29CM', 'INOOINT'])

export interface ReturnShipperDisplay {
  shipperName?: string | null
}

export const toReturnShipper = (value: string, enabled: boolean): string => (enabled && TARGET_SHIPPERS.has(value) ? 'MUSINSA' : value)

export const toReturnShippers = (values: string[], enabled: boolean): string[] => enabled ? [...new Set(values.map((value) => toReturnShipper(value, enabled)))] : values

export const toReturnShipperNames = (values: string[], enabled: boolean, shippers: readonly ShipperItem[] = []): string[] => {
  if (!enabled) return values
  const aliases = new Set(shippers.filter((shipper) => TARGET_SHIPPERS.has(shipper.shipperCode)).map((shipper) => shipper.shipperName))
  return [...new Set(values.map((value) => aliases.has(value) ? 'MUSINSA' : toReturnShipper(value, true)))]
}

export const groupReturnRowKeys = (
  rows: { rowKey: string; sprNm?: string | null }[],
  keys: ReadonlySet<string>,
  fallback: string,
  enabled: boolean,
): Map<string, Set<string>> => {
  const groups = new Map<string, Set<string>>()
  rows.filter((row) => keys.has(row.rowKey)).forEach((row) => {
    const shipper = (enabled && row.sprNm) || fallback
    const group = groups.get(shipper) ?? new Set<string>()
    group.add(row.rowKey)
    groups.set(shipper, group)
  })
  return groups
}

/** 반품 화면 전용 필드를 두지 않고 BE 입고·반품(LOGISPRD-14784)과 같은 `inboundEnabled` 를 읽는다. 켜고 끌 때 함께 움직인다. */
export const useCoMingleReturns = (): boolean => {
  const flag = useFeatureFlagBoolean('feature.coMingle', 'inboundEnabled')
  return !flag.isPending && !flag.isError && flag.data === true
}

export interface CoMingleReturnProps {
  coMingleEnabled: boolean
  originalShippers?: ShipperItem[]
}

/** 필터의 최초값과 URL 자동 조회가 같은 플래그 값을 사용하도록 첫 조회를 기다린다. */
export const withCoMingleReturns = (Screen: ComponentType<CoMingleReturnProps>, needsShipperNames = false): ComponentType => {
  const NamedReturnScreen = () => {
    const originals = useQuery({
      queryKey: ['return', 'originalShipperNames'],
      queryFn: async () => (await getShipperListV2({ shipperNames: [], logisticsServiceTypes: [], page: 0, size: 10000 })).data.items,
      staleTime: 1000 * 60 * 10,
      meta: { noErrorMessage: true },
    })
    useEffect(() => {
      if (originals.error) notice.failure(MESSAGES.shippersFailed)
    }, [originals.error])
    // 원본 화주명을 모르면 URL 조건을 잘못 바꿔 자동 조회하므로 진입하지 않고 재조회만 연다.
    if (originals.isError && !originals.data) return createElement(Button, { kind: 'primary_light', onClick: () => { originals.refetch().catch(() => undefined) } }, '다시 조회')
    if (originals.isPending) return createElement(Loader)
    return createElement(Screen, { coMingleEnabled: true, originalShippers: originals.data })
  }
  NamedReturnScreen.displayName = `CoMingleReturns(${Screen.displayName ?? Screen.name}).Named`
  const ReturnScreen = () => {
    const flag = useFeatureFlagBoolean('feature.coMingle', 'inboundEnabled') // useCoMingleReturns 와 같은 필드
    const enabled = useRef<boolean>()
    if (flag.isPending) return createElement(Loader)
    // 편집 중 플래그 재조회로 최초 필터와 저장 대상이 바뀌지 않도록 화면 진입 값으로 고정한다.
    enabled.current ??= !flag.isError && flag.data === true
    if (needsShipperNames && enabled.current) return createElement(NamedReturnScreen)
    return createElement(Screen, { coMingleEnabled: enabled.current })
  }
  ReturnScreen.displayName = `CoMingleReturns(${Screen.displayName ?? Screen.name})`
  return ReturnScreen
}
